import React, { useState, useRef, useEffect } from "react";
import { useAuth, API } from "@/context/AuthContext";
import { INTENT_COLORS, INTENT_LABELS, PLATFORM_LABELS, MODE_LABELS } from "@/lib/utils-buddy";
import Navbar from "@/components/Navbar";
import UsageRing from "@/components/UsageRing";
import { motion, AnimatePresence } from "framer-motion";
import {
  Copy, Check, Star, RotateCcw, MessageSquare, Mail, Linkedin, Instagram, Heart,
  Zap, Shield, Sparkles, HandHeart, Laugh, Briefcase, Minus, Bot,
  Send, BookOpen, Languages, Layers, Lock, X, Share2, Search, Target,
  Mic, MicOff, Coffee, SmilePlus, HeartHandshake
} from "lucide-react";

const INTENT_ICONS = {
  confident: Zap, polite: HandHeart, genz: Sparkles, apologize: Shield,
  flirty: Laugh, professional: Briefcase, short: Minus, humanize: Bot,
  casual: Coffee, funny: SmilePlus, caring: HeartHandshake,
};
const PLATFORM_ICONS = {
  whatsapp: MessageSquare, email: Mail, linkedin: Linkedin, instagram: Instagram, dating: Heart,
};

const TRANSLATE_LANGS = [
  "English", "Spanish", "French", "German", "Portuguese", "Italian", "Japanese", "Korean",
  "Chinese", "Arabic", "Hindi", "Russian", "Dutch", "Turkish", "Swedish", "Polish",
  "Thai", "Vietnamese", "Indonesian", "Malay", "Filipino",
];

const UPDATED_MODES = {
  ...MODE_LABELS,
  translate: "Translate",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [intent, setIntent] = useState("confident");
  const [platform, setPlatform] = useState("");
  const [mode, setMode] = useState("rewrite");
  const [replyContext, setReplyContext] = useState("");
  const [showReply, setShowReply] = useState(false);
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [usage, setUsage] = useState(null);
  const [variations, setVariations] = useState(null);
  const [variationsLoading, setVariationsLoading] = useState(false);
  const [variationsError, setVariationsError] = useState("");
  const [savedToLibrary, setSavedToLibrary] = useState(false);
  const [targetLang, setTargetLang] = useState("Spanish");
  const [score, setScore] = useState(null);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [toneCheck, setToneCheck] = useState(null);
  const [toneCheckLoading, setToneCheckLoading] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);
  const [showToneInput, setShowToneInput] = useState(false);
  const [toneText, setToneText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const textareaRef = useRef(null);
  const plan = user?.plan || "free";
  const isPro = plan === "pro" || plan === "teams";

  useEffect(() => { fetchUsage(); }, []);

  const fetchUsage = async () => {
    try { const { data } = await API.get("/usage"); setUsage(data); } catch {}
  };

  const startRecording = async () => {
    try {
      // Enforce free-tier voice quota (2 lifetime for free, unlimited for Pro/Teams).
      // Server responds with 402 limit_reached which the axios interceptor turns
      // into the LimitReachedModal automatically.
      try { await API.post("/voice/track"); }
      catch (err) { if (err?.response?.status === 402 || err?.response?.status === 401) return; }

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        alert("Speech recognition is not supported in this browser. Please use Chrome.");
        return;
      }
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";
      let finalTranscript = text;
      recognition.onresult = (event) => {
        let interim = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript + " ";
          } else {
            interim += event.results[i][0].transcript;
          }
        }
        setText(finalTranscript + interim);
      };
      recognition.onerror = () => { setIsRecording(false); };
      recognition.onend = () => { setIsRecording(false); };
      recognition.start();
      setMediaRecorder(recognition);
      setIsRecording(true);
    } catch {
      alert("Microphone access denied. Please allow microphone permission.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setMediaRecorder(null);
    }
    setIsRecording(false);
  };

  const handleRewrite = async () => {
    if (!text.trim() && mode !== "reply") return;
    setLoading(true);
    setResult("");
    setVariations(null);
    setVariationsError("");
    setSavedToLibrary(false);
    setScore(null);
    try {
      const { data } = await API.post("/rewrite", {
        text: text.trim(), intent, platform, mode,
        reply_context: mode === "reply" ? replyContext : null,
        target_language: mode === "translate" ? targetLang : null,
      });
      setResult(data.rewritten_text);
      fetchUsage();
      // Auto-fetch confidence score for Pro+
      if (isPro && data.rewritten_text) {
        fetchScore(data.rewritten_text);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "daily_limit_reached") {
        setResult(`Daily limit reached (${detail.used}/${detail.limit}). Upgrade your plan for more rewrites!`);
      } else if (typeof detail === "string") {
        setResult(detail);
      } else {
        setResult("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVariations = async () => {
    if (!text.trim()) return;
    // Reply mode requires a context — block the call rather than send an empty one
    if (mode === "reply" && !replyContext.trim()) {
      setVariationsError("Paste the message you're replying to first.");
      return;
    }
    setVariationsLoading(true);
    setVariations(null);
    setVariationsError("");
    try {
      const body = { text: text.trim(), intent, platform, mode };
      if (mode === "reply" && replyContext.trim()) body.reply_context = replyContext.trim();
      if (mode === "translate" && targetLang) body.target_language = targetLang;
      const { data } = await API.post("/rewrite/variations", body);
      setVariations(data.variations);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setVariationsError(typeof detail === "string" ? detail : "Could not generate variations. Try again.");
      setVariations(null);
    } finally {
      setVariationsLoading(false);
    }
  };

  const handleSaveToLibrary = async () => {
    if (!result) return;
    try {
      await API.post("/library", { content: result, intent, platform });
      setSavedToLibrary(true);
    } catch {}
  };

  const handleCopy = (txt) => {
    try { navigator.clipboard.writeText(txt || result); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const fetchScore = async (rewrittenText) => {
    setScoreLoading(true);
    try {
      const { data } = await API.post("/rewrite/score", { text: rewrittenText, intent, platform });
      setScore(data);
    } catch { setScore(null); }
    finally { setScoreLoading(false); }
  };

  const handleToneCheck = async () => {
    if (!toneText.trim()) return;
    setToneCheckLoading(true);
    setToneCheck(null);
    try {
      const { data } = await API.post("/tone-check", { text: toneText.trim() });
      setToneCheck(data);
    } catch { setToneCheck(null); }
    finally { setToneCheckLoading(false); }
  };

  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-cream)" }}>
      <Navbar usage={usage} />
      <main className="max-w-5xl mx-auto px-4 pt-6 pb-16">
        {/* Header */}
        <motion.div className="mb-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="font-display text-2xl font-semibold" style={{ color: "var(--text-primary)" }}>
            Hey{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>What would you like to say today?</p>
        </motion.div>

        <div className="grid lg:grid-cols-[1fr,400px] gap-6">
          {/* ─── Left: Editor ─── */}
          <motion.div className="space-y-5" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            {/* Mode */}
            <div>
              <span className="label-caps block mb-2">Mode</span>
              <div className="flex flex-wrap gap-2">
                {Object.entries(UPDATED_MODES).map(([key, label]) => (
                  <motion.button
                    key={key}
                    onClick={() => { setMode(key); if (key === "reply") setShowReply(true); else setShowReply(false); }}
                    className="platform-pill"
                    style={mode === key ? { background: "var(--rolex-green)", color: "white", borderColor: "var(--rolex-green)" } : {}}
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.97 }}
                    data-testid={`mode-${key}`}
                  >
                    {key === "translate" && <Languages size={13} />}
                    {label}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Translate Language */}
            {mode === "translate" && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                <span className="label-caps block mb-2">Translate To</span>
                <select
                  className="buddy-input"
                  value={targetLang}
                  onChange={(e) => setTargetLang(e.target.value)}
                  data-testid="translate-lang-select"
                >
                  {TRANSLATE_LANGS.map(lang => <option key={lang} value={lang}>{lang}</option>)}
                </select>
              </motion.div>
            )}

            {/* Reply Context */}
            {showReply && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
                <span className="label-caps block mb-2">Message you received</span>
                <textarea className="buddy-input resize-none" rows={3} placeholder="Paste the message you want to reply to..." value={replyContext} onChange={(e) => setReplyContext(e.target.value)} data-testid="reply-context-input" />
              </motion.div>
            )}

            {/* Main Input */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="label-caps">{mode === "reply" ? "Your reply idea (optional)" : "Your message"}</span>
                <motion.button
                  onClick={isRecording ? stopRecording : startRecording}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                  style={isRecording
                    ? { background: "var(--error-bg)", color: "var(--error)", border: "1px solid var(--error)" }
                    : { background: "var(--surface-warm)", color: "var(--text-secondary)", border: "1px solid var(--surface-border)" }
                  }
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  data-testid="mic-btn"
                >
                  {isRecording ? <><MicOff size={13} /> Stop</> : <><Mic size={13} /> Speak</>}
                </motion.button>
              </div>
              <div className="relative">
                {isRecording && (
                  <div className="absolute top-2 left-3 flex items-center gap-1.5 z-10">
                    <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "var(--error)" }} />
                    <span className="text-[10px] font-medium" style={{ color: "var(--error)" }}>Listening...</span>
                  </div>
                )}
                <textarea
                  ref={textareaRef}
                  className="buddy-input resize-none"
                  style={{ minHeight: "120px", maxHeight: "320px", paddingTop: isRecording ? "28px" : "12px" }}
                  rows={5}
                  placeholder={mode === "reply" ? "Rough idea for your reply..." : "Type, paste, or tap Speak to use your voice..."}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  data-testid="rewrite-input"
                />
                <span className="absolute bottom-3 right-3 text-xs" style={{ color: "var(--text-light)" }}>{wordCount} words</span>
              </div>
            </div>

            {/* Platform */}
            <div>
              <span className="label-caps block mb-2">Platform</span>
              <div className="flex flex-wrap gap-2">
                {Object.entries(PLATFORM_LABELS).map(([key, label]) => {
                  const Icon = PLATFORM_ICONS[key];
                  return (
                    <motion.button key={key} onClick={() => setPlatform(prev => prev === key ? "" : key)} className="platform-pill" whileHover={{ y: -1 }} whileTap={{ scale: 0.97 }}
                      style={platform === key ? { background: "var(--rolex-green)", color: "white", borderColor: "var(--rolex-green)" } : {}}
                      data-testid={`platform-${key}`}>
                      <Icon size={14} />{label}
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Intent Grid */}
            <div>
              <span className="label-caps block mb-2">Emotional Tone</span>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(INTENT_LABELS).map(([key, label]) => {
                  const colors = INTENT_COLORS[key];
                  const Icon = INTENT_ICONS[key];
                  const isActive = intent === key;
                  return (
                    <motion.button key={key} onClick={() => setIntent(key)} className="intent-chip" whileHover={{ y: -2, scale: 1.02 }} whileTap={{ scale: 0.97 }}
                      style={isActive ? { background: colors.bg, color: "white", borderColor: colors.border } : { background: "var(--surface-white)", color: colors.text, borderColor: colors.border + "40" }}
                      data-testid={`intent-${key}`}>
                      <Icon size={14} />{label}
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Rewrite Button */}
            <motion.button
              onClick={handleRewrite}
              disabled={loading || (!text.trim() && mode !== "reply")}
              className="btn-primary w-full py-3.5 text-base flex items-center justify-center gap-2"
              style={{ opacity: loading || (!text.trim() && mode !== "reply") ? 0.6 : 1 }}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              data-testid="rewrite-btn"
            >
              {loading ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Send size={18} />Rewrite with Buddy</>}
            </motion.button>
          </motion.div>

          {/* ─── Right: Output + Usage + Variations ─── */}
          <motion.div className="space-y-5" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
            {/* Usage Widget */}
            {usage && (
              <div className="p-5 rounded-2xl" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}>
                <span className="label-caps block mb-3">Today's Usage</span>
                <div className="flex items-center gap-4">
                  <UsageRing used={usage.rewrites_used} limit={usage.rewrites_limit} />
                  <div>
                    <p className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>
                      {usage.rewrites_limit === -1 ? "Unlimited" : `${usage.rewrites_used} / ${usage.rewrites_limit}`} rewrites
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{plan.charAt(0).toUpperCase() + plan.slice(1)} plan</p>
                  </div>
                </div>
              </div>
            )}

            {/* Output */}
            <div className="p-5 rounded-2xl" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)", minHeight: "200px" }}>
              <div className="flex items-center justify-between mb-3">
                <span className="label-caps">Result</span>
                <div className="flex items-center gap-2">
                  {result && !loading && isPro && (
                    <motion.button
                      onClick={handleSaveToLibrary}
                      className="flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md transition-all"
                      style={{ background: savedToLibrary ? "var(--success-bg)" : "var(--surface-warm)", color: savedToLibrary ? "var(--success)" : "var(--rolex-gold)" }}
                      whileHover={{ scale: 1.04 }}
                      data-testid="save-to-library-btn"
                    >
                      {savedToLibrary ? <Check size={12} /> : <BookOpen size={12} />}
                      {savedToLibrary ? "Saved" : "Save"}
                    </motion.button>
                  )}
                  {result && !loading && (
                    <motion.button
                      onClick={() => handleCopy()}
                      className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md transition-all"
                      style={{ background: copied ? "var(--success-bg)" : "var(--surface-warm)", color: copied ? "var(--success)" : "var(--text-secondary)" }}
                      whileHover={{ scale: 1.04 }}
                      data-testid="copy-result-btn"
                    >
                      {copied ? <Check size={13} /> : <Copy size={13} />}
                      {copied ? "Copied!" : "Copy"}
                    </motion.button>
                  )}
                </div>
              </div>
              {loading ? (
                <div className="space-y-3">
                  <div className="h-4 rounded animate-shimmer" style={{ width: "90%" }} />
                  <div className="h-4 rounded animate-shimmer" style={{ width: "75%" }} />
                  <div className="h-4 rounded animate-shimmer" style={{ width: "60%" }} />
                </div>
              ) : result ? (
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[15px] leading-relaxed whitespace-pre-wrap flex-1" style={{ color: "var(--text-secondary)" }} data-testid="rewrite-result">
                      {result}
                    </motion.p>
                    {/* Confidence Score Badge */}
                    {isPro && score && !scoreLoading && (
                      <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="shrink-0 w-12 h-12 rounded-full flex flex-col items-center justify-center"
                        style={{ background: score.score >= 8 ? "var(--rolex-green-light)" : score.score >= 6 ? "var(--rolex-gold-light)" : "var(--error-bg)", border: `2px solid ${score.score >= 8 ? "var(--rolex-green)" : score.score >= 6 ? "var(--rolex-gold)" : "var(--error)"}` }}
                        title={score.issue || "Confidence score"}
                        data-testid="confidence-badge"
                      >
                        <span className="text-sm font-bold" style={{ color: score.score >= 8 ? "var(--rolex-green)" : score.score >= 6 ? "var(--rolex-gold)" : "var(--error)" }}>{score.score}</span>
                        <span className="text-[7px] font-medium" style={{ color: "var(--text-muted)" }}>/10</span>
                      </motion.div>
                    )}
                    {isPro && scoreLoading && (
                      <div className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center" style={{ border: "2px solid var(--surface-border)" }}>
                        <span className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--rolex-green)", borderTopColor: "transparent" }} />
                      </div>
                    )}
                  </div>
                  {isPro && score?.issue && (
                    <p className="text-[11px] mt-2 px-2 py-1 rounded inline-block" style={{ background: "var(--warning-bg)", color: "var(--warning)" }} data-testid="score-issue">{score.issue}</p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Sparkles size={32} style={{ color: "var(--surface-border-mid)" }} />
                  <p className="text-sm mt-3" style={{ color: "var(--text-light)" }}>Your rewritten message will appear here</p>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            {result && !loading && (
              <div className="flex gap-2">
                <motion.button onClick={handleRewrite} className="btn-secondary flex-1 flex items-center justify-center gap-1.5 py-2 text-sm" whileHover={{ scale: 1.02 }} data-testid="retry-btn">
                  <RotateCcw size={14} />Try Again
                </motion.button>
                {isPro && (
                  <motion.button onClick={handleVariations} disabled={variationsLoading} className="btn-secondary flex-1 flex items-center justify-center gap-1.5 py-2 text-sm" whileHover={{ scale: 1.02 }} data-testid="variations-btn">
                    {variationsLoading ? <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <Layers size={14} />}
                    Variations
                  </motion.button>
                )}
                <motion.button onClick={() => setShowShareCard(true)} className="btn-secondary flex-1 flex items-center justify-center gap-1.5 py-2 text-sm" whileHover={{ scale: 1.02 }} data-testid="share-card-btn">
                  <Share2 size={14} />Share
                </motion.button>
              </div>
            )}

            {/* Share Card Modal */}
            <AnimatePresence>
              {showShareCard && result && (
                <motion.div
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 flex items-center justify-center p-4"
                  style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)" }}
                  onClick={() => setShowShareCard(false)}
                >
                  <motion.div
                    initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
                    className="w-full max-w-md rounded-2xl overflow-hidden"
                    style={{ background: "var(--surface-cream)" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="p-6" style={{ background: "var(--rolex-green)" }}>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display text-lg font-semibold text-white">Buddy</h3>
                        <button onClick={() => setShowShareCard(false)} className="text-white/60 hover:text-white"><X size={18} /></button>
                      </div>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.8)" }}>BEFORE</span>
                      <p className="text-sm leading-relaxed text-white/80 mt-2">{text.slice(0, 150)}{text.length > 150 ? "..." : ""}</p>
                    </div>
                    <div className="p-6">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--rolex-green-light)", color: "var(--rolex-green)" }}>AFTER — {INTENT_LABELS[intent]?.toUpperCase()}</span>
                      <p className="text-sm leading-relaxed mt-2" style={{ color: "var(--text-primary)" }}>{result.slice(0, 200)}{result.length > 200 ? "..." : ""}</p>
                      {score && <p className="text-xs mt-3" style={{ color: "var(--rolex-gold)" }}>Confidence: {score.score}/10</p>}
                      <div className="mt-4 pt-3 flex items-center justify-between" style={{ borderTop: "1px solid var(--surface-border)" }}>
                        <p className="text-[10px]" style={{ color: "var(--text-light)" }}>Made with Buddy — buddy.app</p>
                        <motion.button
                          onClick={() => handleCopy(`Before: ${text}\n\nAfter (${INTENT_LABELS[intent]}): ${result}\n\nMade with Buddy — buddy.app`)}
                          className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-md"
                          style={{ background: "var(--rolex-green)", color: "white" }}
                          whileHover={{ scale: 1.04 }}
                          data-testid="share-copy-btn"
                        >
                          <Copy size={12} /> Copy Card
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Variations error (Pro+) */}
            {isPro && variationsError && !variations && (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="p-3 rounded-xl text-xs flex items-start gap-2"
                style={{ background: "var(--error-bg)", color: "var(--error)", border: "1px solid var(--error)" }}
                data-testid="variations-error"
              >
                <span className="shrink-0 mt-0.5">⚠</span>{variationsError}
              </motion.div>
            )}

            {/* Variations (Pro+) */}
            {isPro && variations && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                <span className="label-caps block">Tone Variations</span>
                {variations.map((v, i) => (
                  <motion.div
                    key={i}
                    className="p-4 rounded-xl cursor-pointer"
                    style={{ background: "rgba(255,255,255,0.6)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.4)" }}
                    whileHover={{ y: -2, boxShadow: "0 6px 20px rgba(0,0,0,0.06)" }}
                    onClick={() => handleCopy(v.text)}
                    data-testid={`variation-${i}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--rolex-green-light)", color: "var(--rolex-green)" }}>{v.label}</span>
                      <Copy size={12} style={{ color: "var(--text-light)" }} />
                    </div>
                    <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{v.text}</p>
                  </motion.div>
                ))}
              </motion.div>
            )}

            {/* Free user - locked variations hint */}
            {!isPro && result && !loading && (
              <motion.div
                className="p-4 rounded-xl text-center relative overflow-hidden"
                style={{ background: "rgba(255,255,255,0.4)", backdropFilter: "blur(8px)", border: "1px dashed var(--rolex-gold)", opacity: 0.7 }}
                initial={{ opacity: 0 }} animate={{ opacity: 0.7 }}
              >
                <Lock size={18} style={{ color: "var(--rolex-gold)" }} className="mx-auto mb-2" />
                <p className="text-xs font-medium" style={{ color: "var(--rolex-gold)" }}>Upgrade to Pro to see 3 tone variations</p>
              </motion.div>
            )}

            {/* ─── Tone Check ─── */}
            <div className="p-5 rounded-2xl" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}>
              <button
                onClick={() => setShowToneInput(!showToneInput)}
                className="flex items-center gap-2 w-full text-left"
                data-testid="tone-check-toggle"
              >
                <Target size={16} style={{ color: "var(--rolex-green)" }} />
                <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>Tone Check</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full ml-auto" style={{ background: "var(--rolex-green-light)", color: "var(--rolex-green)" }}>AI</span>
              </button>
              <AnimatePresence>
                {showToneInput && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <p className="text-xs mt-3 mb-2" style={{ color: "var(--text-muted)" }}>Paste a message you received to analyze its emotional tone</p>
                    <textarea
                      className="buddy-input resize-none text-sm"
                      rows={3}
                      placeholder="Paste the message you want to analyze..."
                      value={toneText}
                      onChange={(e) => setToneText(e.target.value)}
                      data-testid="tone-check-input"
                    />
                    <motion.button
                      onClick={handleToneCheck}
                      disabled={!toneText.trim() || toneCheckLoading}
                      className="btn-primary py-2 px-4 text-xs mt-2 flex items-center gap-1.5"
                      style={{ opacity: !toneText.trim() ? 0.5 : 1 }}
                      whileHover={{ scale: 1.02 }}
                      data-testid="tone-check-btn"
                    >
                      {toneCheckLoading ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Search size={13} />}
                      Analyze Tone
                    </motion.button>
                    {toneCheck && (
                      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-3 p-3 rounded-xl" style={{ background: "var(--surface-warm)" }}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--rolex-green)", color: "white" }}>{toneCheck.primary_tone?.toUpperCase()}</span>
                          {toneCheck.secondary_tone && (
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: "var(--surface-border)", color: "var(--text-secondary)" }}>{toneCheck.secondary_tone}</span>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-2 mb-2">
                          <div className="text-center">
                            <p className="text-[10px]" style={{ color: "var(--text-light)" }}>Formality</p>
                            <p className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>{toneCheck.formality}/10</p>
                          </div>
                          <div className="text-center">
                            <p className="text-[10px]" style={{ color: "var(--text-light)" }}>Sentiment</p>
                            <p className="text-sm font-bold" style={{ color: toneCheck.sentiment === "positive" ? "var(--rolex-green)" : toneCheck.sentiment === "negative" ? "var(--error)" : "var(--text-muted)" }}>{toneCheck.sentiment}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-[10px]" style={{ color: "var(--text-light)" }}>Energy</p>
                            <p className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>{toneCheck.energy}</p>
                          </div>
                        </div>
                        <p className="text-xs leading-relaxed" style={{ color: "var(--text-muted)" }} data-testid="tone-check-summary">{toneCheck.summary}</p>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
